import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  DatePicker,
  Button,
  Space,
  message,
  Typography,
  Tabs,
  Select,
  TreeSelect,
  Tag,
  Divider
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  UserAddOutlined,
  DownloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import customerRelationshipService from '../services/customerRelationshipService';
import EmployeeSelect from '../components/common/EmployeeSelect';
import weworkService from '../services/weworkService';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title } = Typography;

const CustomerRelationshipAnalysis = () => {
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('day'),
    dayjs().endOf('day')
  ]);

  // 员工维度数据
  const [employeeData, setEmployeeData] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employees, setEmployees] = useState([]);

  // 标签维度数据
  const [tagData, setTagData] = useState([]);
  const [tagGroups, setTagGroups] = useState([]); // 标签组+子标签
  const [selectedTagGroupIds, setSelectedTagGroupIds] = useState([]); // 选中的标签组
  const [excludeTagIds, setExcludeTagIds] = useState([]); // 排除的标签

  // 概览数据
  const [overviewData, setOverviewData] = useState({
    totalFriends: 0,
    retainedFriends: 0,
    periodAddCount: 0
  });

  // 当前Tab
  const [activeTab, setActiveTab] = useState('employee');

  // 加载标签组列表
  useEffect(() => {
    const fetchTagGroups = async () => {
      try {
        const groups = await customerRelationshipService.getTagGroupsWithTags();
        setTagGroups(groups || []);
      } catch (error) {
        console.error('获取标签组列表失败:', error);
      }
    };
    fetchTagGroups();
  }, []);

  // 加载员工列表
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const employeeList = await weworkService.getAllEmployees();
        setEmployees(employeeList || []);
      } catch (error) {
        console.error('获取员工列表失败:', error);
      }
    };
    fetchEmployees();
  }, []);

  // 时间快捷选项
  const rangePresets = [
    { label: '今天', value: [dayjs().startOf('day'), dayjs().endOf('day')] },
    { label: '昨天', value: [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')] },
    { label: '近3天', value: [dayjs().subtract(2, 'days').startOf('day'), dayjs().endOf('day')] },
    { label: '最近1个月', value: [dayjs().subtract(1, 'month').startOf('day'), dayjs().endOf('day')] },
    { label: '最近3个月', value: [dayjs().subtract(3, 'months').startOf('day'), dayjs().endOf('day')] },
    { label: '最近1年', value: [dayjs().subtract(1, 'year').startOf('day'), dayjs().endOf('day')] },
  ];

  // 构建 TreeSelect 数据（标签组 → 子标签）
  const tagGroupTreeData = tagGroups.map(group => ({
    title: group.groupName,
    value: `group-${group.groupId}`,
    key: `group-${group.groupId}`,
    children: (group.tags || []).map(tag => ({
      title: tag.name,
      value: `tag-${tag.id}`,
      key: `tag-${tag.id}`,
    }))
  }));

  // 获取所有可排除的标签列表（扁平化，含 groupId）
  const allTagsFlat = tagGroups.flatMap(group =>
    (group.tags || []).map(tag => ({ id: tag.id, name: tag.name, groupId: group.groupId, groupName: group.groupName }))
  );

  // 从 TreeSelect 选中值解析出 tagGroupIds 和 individual tagIds
  const parseTreeSelection = (selectedValues) => {
    const groupIds = [];
    const tagIds = [];
    for (const val of selectedValues) {
      if (typeof val === 'string' && val.startsWith('group-')) {
        groupIds.push(Number(val.replace('group-', '')));
      } else if (typeof val === 'string' && val.startsWith('tag-')) {
        tagIds.push(Number(val.replace('tag-', '')));
      }
    }
    // 如果选了单个标签，找到它所属的 group 加入 groupIds（后端按 group 过滤）
    // 但单个标签选择时，后端会返回整个 group 的标签，所以我们在前端做二次过滤
    return { groupIds, tagIds };
  };

  // 判断一个 tag 是否在 TreeSelect 的选中范围内
  const isTagInSelection = (tagId, selection) => {
    if (!selection || selection.length === 0) return true; // 无选择 = 全部
    const { groupIds, tagIds } = parseTreeSelection(selection);
    if (groupIds.length === 0 && tagIds.length === 0) return true;
    // 检查是否在选中的单个标签中
    if (tagIds.includes(tagId)) return true;
    // 检查是否属于选中的标签组
    const tagInfo = allTagsFlat.find(t => t.id === tagId);
    if (tagInfo && groupIds.includes(tagInfo.groupId)) return true;
    return false;
  };

  // 加载员工维度数据
  const fetchEmployeeData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');

      const result = await customerRelationshipService.getAllMembersOverview(startDate, endDate);

      // 使用后端返回的整体汇总数据（去重后的唯一客户数）
      const overview = result.overview || {};
      setOverviewData({
        totalFriends: overview.totalFriends || 0,
        retainedFriends: overview.retainedFriends || 0,
        periodAddCount: overview.periodAddCount || 0
      });

      // 员工列表数据
      let employeeList = result.employees || [];

      // 按员工筛选
      if (selectedEmployees && selectedEmployees.length > 0) {
        const selectedUserIds = selectedEmployees.map(empId => {
          const employee = employees.find(emp => String(emp.id) === String(empId));
          return employee ? employee.userid : null;
        }).filter(userid => userid !== null);

        employeeList = employeeList.filter(item =>
          selectedUserIds.includes(item.weworkUserId)
        );
      }

      const processedData = employeeList.map((item, index) => ({
        key: index,
        ...item
      }));

      setEmployeeData(processedData);
    } catch (error) {
      console.error('获取员工数据失败:', error);
      message.error('获取员工数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedEmployees, employees]);

  // 加载标签维度数据
  const fetchTagData = useCallback(async () => {
    try {
      setLoading(true);
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');

      const { groupIds, tagIds } = parseTreeSelection(selectedTagGroupIds);

      // 如果选了单个标签（不是整组），把它们所属的 group 也加进去
      const allGroupIds = [...groupIds];
      for (const tagId of tagIds) {
        const tagInfo = allTagsFlat.find(t => t.id === tagId);
        if (tagInfo && !allGroupIds.includes(tagInfo.groupId)) {
          allGroupIds.push(tagInfo.groupId);
        }
      }

      const tagStatistics = await customerRelationshipService.getTagStatistics({
        startDate,
        endDate,
        tagGroupIds: allGroupIds.length > 0 ? allGroupIds : undefined,
        excludeTagIds: excludeTagIds.length > 0 ? excludeTagIds : undefined
      });

      const tagDataWithStats = tagStatistics
        .filter(tag => isTagInSelection(tag.tagId, selectedTagGroupIds))
        .map((tag, index) => ({
          key: index,
          tagId: tag.tagId,
          tagName: tag.tagName,
          totalCustomers: tag.totalCustomers || 0,
          activeCustomers: tag.activeCustomers || 0,
          newCustomers: tag.newCustomers || 0,
        }));

      setTagData(tagDataWithStats);
    } catch (error) {
      console.error('获取标签数据失败:', error);
      message.error('获取标签数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [dateRange, selectedTagGroupIds, excludeTagIds]);

  // 初始加载
  useEffect(() => {
    if (activeTab === 'employee') {
      fetchEmployeeData();
    } else {
      fetchTagData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 标签筛选变化时自动刷新
  useEffect(() => {
    if (activeTab === 'tag') {
      fetchTagData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTagGroupIds, excludeTagIds]);

  // 处理查询
  const handleQuery = () => {
    if (!dateRange || dateRange.length !== 2) {
      message.warning('请选择日期范围');
      return;
    }
    if (activeTab === 'employee') {
      fetchEmployeeData();
    } else {
      fetchTagData();
    }
  };

  // 重置筛选
  const handleReset = async () => {
    const resetDateRange = [dayjs().startOf('day'), dayjs().endOf('day')];
    setDateRange(resetDateRange);

    if (activeTab === 'employee') {
      setSelectedEmployees([]);
      // 直接用重置后的值调用 API
      try {
        setLoading(true);
        const startDate = resetDateRange[0].format('YYYY-MM-DD');
        const endDate = resetDateRange[1].format('YYYY-MM-DD');
        const result = await customerRelationshipService.getAllMembersOverview(startDate, endDate);
        const overview = result.overview || {};
        setOverviewData({
          totalFriends: overview.totalFriends || 0,
          retainedFriends: overview.retainedFriends || 0,
          periodAddCount: overview.periodAddCount || 0
        });
        const employeeList = result.employees || [];
        setEmployeeData(employeeList.map((item, index) => ({ key: index, ...item })));
      } catch (error) {
        message.error('获取员工数据失败');
      } finally {
        setLoading(false);
      }
    } else {
      setSelectedTagGroupIds([]);
      setExcludeTagIds([]);
      try {
        setLoading(true);
        const startDate = resetDateRange[0].format('YYYY-MM-DD');
        const endDate = resetDateRange[1].format('YYYY-MM-DD');
        const tagStatistics = await customerRelationshipService.getTagStatistics({ startDate, endDate });
        setTagData(tagStatistics.map((tag, index) => ({
          key: index, tagId: tag.tagId, tagName: tag.tagName,
          totalCustomers: tag.totalCustomers || 0,
          activeCustomers: tag.activeCustomers || 0,
          newCustomers: tag.newCustomers || 0,
        })));
      } catch (error) {
        message.error('获取标签数据失败');
      } finally {
        setLoading(false);
      }
    }
  };

  // Tab切换
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === 'employee') {
      fetchEmployeeData();
    } else {
      fetchTagData();
    }
  };

  // 导出Excel - 员工维度
  const handleExportEmployee = () => {
    if (employeeData.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }
    try {
      const exportData = employeeData.map(item => ({
        '员工姓名': item.employeeName || '-',
        '部门': item.departmentName || '-',
        '企微好友总数': item.totalCustomers || 0,
        '企微好友留存总数': item.activeCustomers || 0,
        '期间添加': item.recentAddCount || 0,
      }));
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => row[header]).join(','))
      ].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `企微好友分析_员工维度_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请稍后重试');
    }
  };

  // 导出Excel - 标签维度
  const handleExportTag = () => {
    if (tagData.length === 0) {
      message.warning('暂无数据可导出');
      return;
    }
    try {
      const exportData = tagData.map(item => ({
        '标签名称': item.tagName || '-',
        '企微好友总数': item.totalCustomers || 0,
        '企微好友留存总数': item.activeCustomers || 0,
        '期间添加': item.newCustomers || 0,
      }));
      const headers = Object.keys(exportData[0]);
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => headers.map(header => row[header]).join(','))
      ].join('\n');
      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `企微好友分析_标签维度_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('导出成功');
    } catch (error) {
      console.error('导出失败:', error);
      message.error('导出失败，请稍后重试');
    }
  };

  // 员工维度表格列
  const employeeColumns = [
    {
      title: '员工姓名',
      dataIndex: 'employeeName',
      key: 'employeeName',
      fixed: 'left',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '部门',
      dataIndex: 'departmentName',
      key: 'departmentName',
      width: 150,
      render: (text) => text || '-'
    },
    {
      title: '企微好友总数',
      dataIndex: 'totalCustomers',
      key: 'totalCustomers',
      width: 140,
      sorter: (a, b) => (a.totalCustomers || 0) - (b.totalCustomers || 0),
      render: (text) => text || 0
    },
    {
      title: '企微好友留存总数',
      dataIndex: 'activeCustomers',
      key: 'activeCustomers',
      width: 160,
      sorter: (a, b) => (a.activeCustomers || 0) - (b.activeCustomers || 0),
      render: (text) => text || 0
    },
    {
      title: '期间添加',
      dataIndex: 'recentAddCount',
      key: 'recentAddCount',
      width: 120,
      sorter: (a, b) => (a.recentAddCount || 0) - (b.recentAddCount || 0),
      render: (text) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{text || 0}</span>
    },
  ];

  // 标签维度表格列
  const tagColumns = [
    {
      title: '标签名称',
      dataIndex: 'tagName',
      key: 'tagName',
      fixed: 'left',
      width: 150,
      render: (text) => text || '-'
    },
    {
      title: '企微好友总数',
      dataIndex: 'totalCustomers',
      key: 'totalCustomers',
      width: 140,
      sorter: (a, b) => (a.totalCustomers || 0) - (b.totalCustomers || 0),
      render: (text) => text || 0
    },
    {
      title: '企微好友留存总数',
      dataIndex: 'activeCustomers',
      key: 'activeCustomers',
      width: 160,
      sorter: (a, b) => (a.activeCustomers || 0) - (b.activeCustomers || 0),
      render: (text) => text || 0
    },
    {
      title: '期间添加',
      dataIndex: 'newCustomers',
      key: 'newCustomers',
      width: 120,
      sorter: (a, b) => (a.newCustomers || 0) - (b.newCustomers || 0),
      render: (text) => <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{text || 0}</span>
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>企微好友分析</Title>
        </Col>
      </Row>

      {/* 全局筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap>
          <span>时间范围:</span>
          <RangePicker
            value={dateRange}
            onChange={setDateRange}
            format="YYYY-MM-DD HH:mm:ss"
            showTime
            presets={rangePresets}
            style={{ width: 400 }}
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleQuery}
            loading={loading}
          >
            查询
          </Button>
          <Button onClick={handleReset}>
            重置
          </Button>
        </Space>
      </Card>

      {/* 数据概览卡片 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="企微好友总数"
              value={overviewData.totalFriends}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="企微好友留存总数"
              value={overviewData.retainedFriends}
              prefix={<TeamOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Card>
            <Statistic
              title="新增企微好友数"
              value={overviewData.periodAddCount}
              prefix={<UserAddOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 数据明细 - Tabs */}
      <Card title="数据明细">
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <Tabs.TabPane tab="员工维度" key="employee">
            <Space style={{ marginBottom: 16 }} size="middle">
              <span>员工筛选:</span>
              <EmployeeSelect
                multiple
                value={selectedEmployees}
                onChange={setSelectedEmployees}
                placeholder="选择员工（默认全部）"
                allowClear
                style={{ width: 300 }}
                showDisplayModeSwitch={false}
                defaultDisplayMode="department"
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportEmployee}
                disabled={employeeData.length === 0}
              >
                导出Excel
              </Button>
            </Space>
            <Table
              columns={employeeColumns}
              dataSource={employeeData}
              loading={loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                defaultPageSize: 20,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 'max-content' }}
            />
          </Tabs.TabPane>

          <Tabs.TabPane tab="标签维度" key="tag">
            <Space style={{ marginBottom: 16 }} size="middle" wrap>
              <span>标签组筛选:</span>
              <TreeSelect
                treeData={tagGroupTreeData}
                value={selectedTagGroupIds}
                onChange={setSelectedTagGroupIds}
                treeCheckable
                showCheckedStrategy={TreeSelect.SHOW_PARENT}
                placeholder="选择标签组（默认全部）"
                allowClear
                style={{ minWidth: 300 }}
                maxTagCount={3}
                treeDefaultExpandAll={false}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
              />
              <Divider type="vertical" />
              <span>排除标签:</span>
              <Select
                mode="multiple"
                value={excludeTagIds}
                onChange={setExcludeTagIds}
                placeholder="选择要排除的标签"
                allowClear
                showSearch
                optionFilterProp="label"
                style={{ minWidth: 250 }}
                maxTagCount={3}
                options={allTagsFlat.map(tag => ({
                  key: tag.id,
                  value: tag.id,
                  label: `${tag.groupName} / ${tag.name}`,
                }))}
              />
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExportTag}
                disabled={tagData.length === 0}
              >
                导出Excel
              </Button>
            </Space>
            {excludeTagIds.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <span style={{ marginRight: 8, color: '#999' }}>已排除:</span>
                {excludeTagIds.map(tagId => {
                  const tag = allTagsFlat.find(t => t.id === tagId);
                  return tag ? (
                    <Tag
                      key={tagId}
                      closable
                      color="red"
                      onClose={() => setExcludeTagIds(prev => prev.filter(id => id !== tagId))}
                    >
                      {tag.name}
                    </Tag>
                  ) : null;
                })}
              </div>
            )}
            <Table
              columns={tagColumns}
              dataSource={tagData}
              loading={loading}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条记录`,
                defaultPageSize: 20,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
              scroll={{ x: 'max-content' }}
            />
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default CustomerRelationshipAnalysis;
